import type { SearchResultV5 } from '@seek/chalice-types';
import type { AxiosError } from 'axios';
import get from 'lodash/get';

import { isTimeoutError } from 'src/modules/seek-api-request';
import { hashToUserQueryId } from 'src/modules/seek-jobs-analytics';
import createApiClient from 'src/modules/seek-jobs-api-client';

import { selectZoneFeatures } from '../selectors';
import type { TypedThunkAction } from '../types';

import { getPageSize } from './resultsHelpers';
import { selectSearchSource } from './selectors';
import { FETCH_SEARCH_RESULTS_ERROR } from './types';

const api = createApiClient();

export const fetchSearchResults =
  ({
    apiQuery = {},
    brand,
    country = 'AU',
    zone = 'anz-1',
    cookies,
    userQueryId,
    tries = 2,
    timeout,
    userAgent,
    visitorId,
    serverProps,
    isAuthenticated = false,
    relatedSearchesCount,
    newSince,
  }: any): TypedThunkAction<Promise<SearchResultV5>> =>
  (dispatch, getState) => {
    const state = getState();
    const {
      user: { testHeaders, userClientId, sessionId },
      appConfig: { zoneFeatures, locale },
      search,
    } = state;
    const source = selectSearchSource(state);
    const isJobSearchAssistEnabled =
      selectZoneFeatures(state)?.JOBSEARCH_ASSIST;
    const isNudgeIntentEnabled = selectZoneFeatures(state)?.NUDGE_INTENT;
    const pageSize = getPageSize(zoneFeatures);

    return api.jobs
      .search({
        brand,
        searchParams: { ...apiQuery, pageSize },
        country,
        zone,
        cookies,
        userQueryId,
        userClientId,
        sessionId,
        requestId: state.location.requestId,
        timeout,
        userAgent,
        seekerId: get(state, 'user.seekerId'),
        solId: visitorId,
        testHeaders,
        locale,
        serverProps,
        isAuthenticated,
        relatedSearchesCount,
        baseKeywords: search?.baseKeywords,
        source,
        newSince,
        shouldIncludeQueryHints: state.results.shouldIncludeQueryHints,
        isJobSearchAssistEnabled,
        /*
         * Given the feature flag from launch darkly is not accessible
         * in the api layer from the server side, zone features is being used
         * instead to conditionally include the relevant api params for nudge intent.
         *
         *  Note: We will still use the launch darkly feature flag on the client side
         *  to conditionally render the nudge intent component.
         */
        isNudgeIntentEnabled,
      })
      .catch((err: AxiosError) => {
        dispatch({
          type: FETCH_SEARCH_RESULTS_ERROR,
        });

        const retryUserQueryId = hashToUserQueryId({
          query: apiQuery.normalisedQuery,
        });

        if (get(err, 'response.status') === 400) {
          return {
            totalCount: 0,
            data: [],
          } as any;
        }

        if (isTimeoutError(err) || get(err, 'response.status', 0) >= 500) {
          if (tries === 1) {
            throw err;
          }

          return fetchSearchResults({
            apiQuery,
            country,
            zone,
            cookies,
            userQueryId: retryUserQueryId,
            visitorId,
            tries: tries - 1,
            serverProps,
            isAuthenticated,
            newSince,
          })(dispatch, getState, null);
        }

        throw err;
      });
  };
